import { Suspense, useEffect, useState } from 'react';
import { NavLink as EnlaceRuta, Outlet, useLocation, useNavigate } from 'react-router';
import { ActionIcon, AppShell, Badge, Burger, Center, Group, Loader, Menu, NavLink, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuildingBank, IconCalendarDue, IconCategory, IconChecklist, IconChevronDown, IconFolders, IconHelp, IconLayoutDashboard, IconLogout, IconMail, IconRefresh,
  IconScale, IconSparkles, IconUser, IconUserCog,
} from '@tabler/icons-react';
import { etiqueta, ROLES } from '@epms/shared';
import { RUTAS } from '../rutas.ts';
import { AvisoVersion } from './AvisoVersion.tsx';
import { Buscador } from './Buscador.tsx';
import { CintasAlerta } from './CintasAlerta.tsx';
import { VentanaNovedades } from './Novedades.tsx';
import { anotarVisita, menusNuevos } from '../novedades.ts';
import { seccionDeRuta } from '../ayuda/manual.ts';
import { usePerfil, useSalir } from '../sesion.ts';

export function Marco() {
  const [abierto, { toggle, close }] = useDisclosure();
  const { data: perfil } = usePerfil();
  const salir = useSalir();
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const [verNovedades, setVerNovedades] = useState(false);
  const [nuevos, setNuevos] = useState(menusNuevos);
  // Abrir un menú con globito "Nuevo" lo apaga.
  useEffect(() => {
    const menu = [...nuevos].find((r) => pathname === r || pathname.startsWith(r + '/'));
    if (menu) { anotarVisita(menu); setNuevos(menusNuevos()); }
  }, [pathname, nuevos]);

  // Arriba, el uso diario; al pie, la configuración y la sincronización con ARCA.
  interface Enlace { a: string; texto: string; icono: typeof IconFolders; visible: boolean }
  const arriba: Enlace[] = [
    { a: RUTAS.inicio, texto: 'Mi día', icono: IconLayoutDashboard, visible: true },
    { a: RUTAS.clientes, texto: 'Clientes', icono: IconFolders, visible: true },
    { a: RUTAS.bandeja, texto: 'Bandeja de mail', icono: IconMail, visible: perfil?.rol !== 'auxiliar' },
    { a: RUTAS.vencimientos, texto: 'Vencimientos', icono: IconCalendarDue, visible: true },
    { a: RUTAS.conciliar, texto: 'Conciliar', icono: IconScale, visible: true },
    { a: RUTAS.cumplimiento, texto: 'Cumplimiento', icono: IconChecklist, visible: true },
  ];
  const abajo: Enlace[] = [
    { a: RUTAS.configUsuarios, texto: 'Config. usuarios', icono: IconUserCog, visible: perfil?.rol === 'admin' },
    { a: RUTAS.configClientes, texto: 'Config. clientes', icono: IconBuildingBank, visible: true },
    { a: RUTAS.configDocumentos, texto: 'Config. documentos', icono: IconCategory, visible: perfil?.rol === 'admin' },
    { a: RUTAS.sincronizacion, texto: 'Sincronización ARCA', icono: IconRefresh, visible: true },
    { a: RUTAS.ayuda, texto: 'Ayuda', icono: IconHelp, visible: true },
  ];
  const enlace = (e: Enlace) => (
    <NavLink
      key={e.a}
      component={EnlaceRuta}
      to={e.a}
      label={e.texto}
      leftSection={<e.icono size={18} />}
      rightSection={nuevos.has(e.a) ? <Badge size="xs" color="grape" variant="filled">Nuevo</Badge> : undefined}
      active={pathname === e.a || pathname.startsWith(e.a + '/')}
      onClick={close}
    />
  );

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 220, breakpoint: 'sm', collapsed: { mobile: !abierto } }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <Burger opened={abierto} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menú" />
            <Text fw={700} size="lg">epms</Text>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Buscador />
            <Tooltip label="Ayuda de esta pantalla">
              <ActionIcon variant="subtle" size="lg" aria-label="Ayuda de esta pantalla"
                onClick={() => navegar(`${RUTAS.ayuda}?seccion=${seccionDeRuta(pathname)}`)}>
                <IconHelp size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <UnstyledButton>
                <Group gap={6}>
                  <div style={{ textAlign: 'right' }}>
                    <Text size="sm" fw={500}>{perfil?.nombre}</Text>
                    <Text size="xs" c="dimmed">{etiqueta(ROLES, perfil?.rol)}</Text>
                  </div>
                  <IconChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconUser size={14} />} onClick={() => navegar(RUTAS.miCuenta)}>Mi cuenta</Menu.Item>
              <Menu.Item leftSection={<IconSparkles size={14} />} onClick={() => setVerNovedades(true)}>Novedades</Menu.Item>
              <Menu.Item leftSection={<IconHelp size={14} />} onClick={() => navegar(RUTAS.ayuda)}>Ayuda</Menu.Item>
              <Menu.Item leftSection={<IconLogout size={14} />} color="red" onClick={() => void salir()}>Salir</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow>{arriba.filter((e) => e.visible).map(enlace)}</AppShell.Section>
        <AppShell.Section style={{ borderTop: '1px solid var(--mantine-color-default-border)' }} pt="xs">
          {abajo.filter((e) => e.visible).map(enlace)}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        <AvisoVersion />
        <VentanaNovedades />
        {verNovedades && <VentanaNovedades todas alCerrar={() => setVerNovedades(false)} />}
        <CintasAlerta />
        <Suspense fallback={<Center py="xl"><Loader /></Center>}>
          <Outlet />
        </Suspense>
      </AppShell.Main>
    </AppShell>
  );
}
